import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getTodayLocal = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const dateToRefFormat = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${month}-${day}-${year}`;
};

const ZELLE_VENMO_REF_RE = /^(Zelle|Venmo)-\d{2}-\d{2}-\d{4}$/;

// Fallback list if the Settings-managed one hasn't loaded / isn't set.
const DEFAULT_METHODS = [
  { value: 'ACH', label: 'ACH' },
  { value: 'Wire', label: 'Wire' },
  { value: 'CC', label: 'CC (Credit Card)' },
  { value: 'Check', label: 'Check' },
  { value: 'Zelle', label: 'Zelle' },
  { value: 'Venmo', label: 'Venmo' },
  { value: 'Processor', label: 'Processor' },
];

const TransactionFormDialog = ({
  deal,
  transactionDialogOpen,
  setTransactionDialogOpen,
  transactionForm,
  setTransactionForm,
  editingTransaction,
  setEditingTransaction,
  onAddTransaction,
  getAuthHeaders,
  onDeferred,
}) => {
  const isDeferredFee = transactionForm.type === 'DEFERRED_FEE' && !editingTransaction;

  // Payment-method options for the Description dropdown, managed in
  // Settings → Payment Methods. Falls back to the built-in list.
  const [methodOptions, setMethodOptions] = useState(DEFAULT_METHODS);
  useEffect(() => {
    axios
      .get(`${API}/settings/payment-methods`, getAuthHeaders ? getAuthHeaders() : {})
      .then((r) => {
        const list = r.data?.methods;
        if (Array.isArray(list) && list.length) setMethodOptions(list);
      })
      .catch(() => { /* keep defaults */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Deferred-fee defer-source state ─────────────────────────────────────
  // Only relevant when type=DEFERRED_FEE on a NEW row. Edits to existing
  // DEFERRED_FEE rows fall through to the standard onAddTransaction path.
  const [sourceCandidates, setSourceCandidates] = useState([]);
  const [sourceQuery, setSourceQuery] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [sourceDeal, setSourceDeal] = useState(null);
  const [sourceFeesAvailable, setSourceFeesAvailable] = useState(0);
  const [deferAmount, setDeferAmount] = useState('');
  const [submittingDefer, setSubmittingDefer] = useState(false);

  // Reset defer state when the dialog opens or the type changes away.
  useEffect(() => {
    if (!transactionDialogOpen || !isDeferredFee) {
      setSelectedSourceId('');
      setSourceDeal(null);
      setSourceFeesAvailable(0);
      setDeferAmount('');
      setSourceQuery('');
      setSourceCandidates([]);
    }
  }, [transactionDialogOpen, isDeferredFee]);

  // Fetch same-merchant deals for the source picker.
  useEffect(() => {
    if (!transactionDialogOpen || !isDeferredFee || !deal?.company_id || !getAuthHeaders) return;
    let cancelled = false;
    axios.get(`${API}/deals?company_id=${deal.company_id}&limit=200`, { headers: getAuthHeaders() })
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.deals || []);
        setSourceCandidates(list.filter(d => d.id !== deal.id));
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load source deals'); });
    return () => { cancelled = true; };
  }, [transactionDialogOpen, isDeferredFee, deal?.id, deal?.company_id, getAuthHeaders]);

  // Pull source deal's outstanding fees once selected.
  useEffect(() => {
    if (!selectedSourceId || !getAuthHeaders) {
      setSourceDeal(null);
      setSourceFeesAvailable(0);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/deals/${selectedSourceId}`, { headers: getAuthHeaders() })
      .then(res => {
        if (cancelled) return;
        setSourceDeal(res.data);
        const fees = parseFloat(res.data?.total_fees || 0);
        setSourceFeesAvailable(fees);
        setDeferAmount(fees > 0 ? fees.toFixed(2) : '');
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load source deal'); });
    return () => { cancelled = true; };
  }, [selectedSourceId, getAuthHeaders]);

  const filteredCandidates = sourceCandidates.filter(d => {
    if (!sourceQuery.trim()) return true;
    const q = sourceQuery.trim().toLowerCase();
    return (
      String(d.deal_id || '').toLowerCase().includes(q) ||
      (d.deal_name || '').toLowerCase().includes(q) ||
      (d.deal_shortcode || '').toLowerCase().includes(q)
    );
  }).slice(0, 50);

  const handleDeferSubmit = async (e) => {
    e?.preventDefault?.();
    if (!selectedSourceId) { toast.error('Select a source deal'); return; }
    const amt = parseFloat(deferAmount);
    if (!amt || amt <= 0) { toast.error('Defer amount must be greater than zero'); return; }
    if (amt > sourceFeesAvailable + 0.005) {
      toast.error(`Defer amount exceeds source's outstanding fees ($${sourceFeesAvailable.toFixed(2)})`);
      return;
    }
    setSubmittingDefer(true);
    try {
      const r = await axios.post(`${API}/transactions/defer-fee`, {
        target_deal_id: deal.id,
        source_deal_id: selectedSourceId,
        amount: amt,
        date: transactionForm.date,
        reference: transactionForm.reference_number || null,
        notes: transactionForm.notes || null,
      }, { headers: getAuthHeaders() });
      toast.success(`Deferred $${amt.toFixed(2)} from #${sourceDeal?.deal_id} to #${deal.deal_id}`);
      onDeferred?.(r.data);
      setTransactionDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to defer fee');
    } finally {
      setSubmittingDefer(false);
    }
  };

  return (
    <Dialog open={transactionDialogOpen} onOpenChange={(open) => {
      setTransactionDialogOpen(open);
      if (!open) {
        setEditingTransaction(null);
        setTransactionForm({
          date: getTodayLocal(),
          amount: '',
          type: 'PAYMENT',
          description: 'ACH',
          reference_number: '',
          notes: '',
          addBounceFee: true,
          bounceFeeAmount: '35',
          customFeeName: '',
          lawyerGrossCollected: '',
          lawyerFee: ''
        });
      }
    }}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Transaction
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingTransaction ? 'Edit' : 'Add'} Transaction</DialogTitle>
        </DialogHeader>
        <form onSubmit={isDeferredFee ? handleDeferSubmit : onAddTransaction} className="space-y-4">
          {/* Type Selection */}
          <div>
            <Label htmlFor="type">Type</Label>
            <Select
              value={transactionForm.type}
              onValueChange={(value) => {
                let newDescription = '';
                let newAmount = transactionForm.amount;
                if (value === 'Payment') newDescription = 'ACH';
                else if (value === 'Fee' || value === 'Bounced Payment') newDescription = 'R01';
                else if (value === 'REFINANCE_PAYOFF' || value === 'EARLY_PAYOFF') {
                  newAmount = deal?.balance_with_fees?.toString() || deal?.balance?.toString() || '0';
                }
                const shouldAddBounceFee = value === 'ACH_RETURN';
                setTransactionForm({
                  ...transactionForm,
                  type: value,
                  description: newDescription,
                  amount: newAmount,
                  addBounceFee: shouldAddBounceFee,
                  bounceFeeAmount: transactionForm.bounceFeeAmount
                });
              }}
            >
              <SelectTrigger data-testid="transaction-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAYMENT">Payment</SelectItem>
                <SelectItem value="REFUND">Refund</SelectItem>
                <SelectItem value="ACH_RETURN">ACH Return</SelectItem>
                <SelectItem value="DEFAULT_FEE">Fee</SelectItem>
                <SelectItem value="DEFERRED_FEE">Deferred Fee</SelectItem>
                <SelectItem value="LAWYER_COLLECTION">Lawyer Collection</SelectItem>
                <SelectItem value="DISCOUNT">Discount (Payback)</SelectItem>
                <SelectItem value="FEE_DISCOUNT">Discount (Fees)</SelectItem>
                <SelectItem value="CUSTOM_FEE">Custom Fee</SelectItem>
                <SelectItem value="ADJUSTMENT_INCREASE">Manual Adjustment</SelectItem>
                <SelectItem value="WRITE_OFF">Write-Off</SelectItem>
                <SelectItem value="LAWYER_FEE">Lawyer Fee</SelectItem>
                <SelectItem value="REFINANCE_PAYOFF">Refinance Payoff</SelectItem>
                <SelectItem value="EARLY_PAYOFF">Early Payoff</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Lawyer Collection Fields — Gross / Fee% / Net layout */}
          {transactionForm.type === 'LAWYER_COLLECTION' && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3">
              <p className="text-xs font-medium text-blue-700">Lawyer Collection — enter Gross + Fee%; Net to PFS is computed.</p>
              <div>
                <Label htmlFor="gross_collected">Gross Collected <span className="text-red-500">*</span></Label>
                <Input
                  id="gross_collected"
                  data-testid="lawyer-gross-collected-input"
                  type="number" step="0.01" min="0.01"
                  placeholder="Total amount collected from merchant"
                  value={transactionForm.lawyerGrossCollected || ''}
                  onChange={(e) => {
                    const gross = e.target.value;
                    const pct = parseFloat(transactionForm.lawyerFeePct ?? 25) || 0;
                    const fee = gross ? ((parseFloat(gross) * pct) / 100).toFixed(2) : '';
                    setTransactionForm({
                      ...transactionForm,
                      lawyerGrossCollected: gross,
                      amount: gross,
                      lawyerFee: fee,
                    });
                  }}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="lawyer_fee_pct">Lawyer Fee %</Label>
                  <Input
                    id="lawyer_fee_pct"
                    data-testid="lawyer-fee-pct-input"
                    type="number" step="0.5" min="0" max="100"
                    placeholder="e.g. 25"
                    value={transactionForm.lawyerFeePct ?? '25'}
                    onChange={(e) => {
                      const pct = e.target.value;
                      const gross = parseFloat(transactionForm.lawyerGrossCollected) || 0;
                      const fee = gross ? ((gross * (parseFloat(pct) || 0)) / 100).toFixed(2) : '';
                      setTransactionForm({
                        ...transactionForm,
                        lawyerFeePct: pct,
                        lawyerFee: fee,
                      });
                    }}
                  />
                </div>
                <div>
                  <Label>Net to PFS</Label>
                  <Input
                    type="text"
                    readOnly
                    className="bg-white font-semibold"
                    data-testid="lawyer-net-display"
                    value={(() => {
                      const gross = parseFloat(transactionForm.lawyerGrossCollected) || 0;
                      const fee = parseFloat(transactionForm.lawyerFee) || 0;
                      const net = gross - fee;
                      return net > 0 ? `$${net.toFixed(2)}` : '$0.00';
                    })()}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lawyer_fee">Lawyer Fee ($)</Label>
                <Input
                  id="lawyer_fee"
                  data-testid="lawyer-fee-input"
                  type="number" step="0.01" min="0"
                  placeholder="Auto-calculated from Fee %"
                  value={transactionForm.lawyerFee || ''}
                  onChange={(e) => setTransactionForm({ ...transactionForm, lawyerFee: e.target.value, lawyerFeePct: '' })}
                />
                <p className="text-xs text-gray-500 mt-1">Override fee directly if needed (clears Fee %).</p>
              </div>
            </div>
          )}

          {/* Payment Description */}
          {transactionForm.type === 'PAYMENT' && (
            <div>
              <Label htmlFor="description">Description</Label>
              <Select
                value={transactionForm.description}
                onValueChange={(value) => {
                  const newForm = { ...transactionForm, description: value };
                  if (value === 'Zelle' || value === 'Venmo') {
                    const dateStr = transactionForm.date || getTodayLocal();
                    const refSuffix = dateToRefFormat(dateStr);
                    if (!transactionForm.reference_number || ZELLE_VENMO_REF_RE.test(transactionForm.reference_number)) {
                      newForm.reference_number = `${value}-${refSuffix}`;
                    }
                    const prevNoteAutoSet = transactionForm.notes === 'Zelle' || transactionForm.notes === 'Venmo';
                    if (!transactionForm.notes || prevNoteAutoSet) newForm.notes = value;
                  }
                  setTransactionForm(newForm);
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select description" /></SelectTrigger>
                <SelectContent>
                  {methodOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label || m.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Fee Reason */}
          {transactionForm.type === 'DEFAULT_FEE' && (
            <div>
              <Label htmlFor="description">Fee Reason</Label>
              <Select
                value={transactionForm.description}
                onValueChange={(value) => setTransactionForm({...transactionForm, description: value})}
              >
                <SelectTrigger data-testid="bounce-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="R01">R01 - Bounced Payment</SelectItem>
                  <SelectItem value="R02">R02 - Account Closed</SelectItem>
                  <SelectItem value="R03">R03 - No Account Found</SelectItem>
                  <SelectItem value="R04">R04 - Invalid Account Number</SelectItem>
                  <SelectItem value="R05">R05 - Unauthorized</SelectItem>
                  <SelectItem value="R08">R08 - Stopped Payment</SelectItem>
                  <SelectItem value="R09">R09 - Uncollected Funds</SelectItem>
                  <SelectItem value="R16">R16 - Account Frozen</SelectItem>
                  <SelectItem value="R20">R20 - Non-Transactional Account</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom Fee Name */}
          {transactionForm.type === 'CUSTOM_FEE' && (
            <div>
              <Label htmlFor="customFeeName">Fee Name <span className="text-red-500">*</span></Label>
              <Input
                id="customFeeName"
                data-testid="custom-fee-name-input"
                placeholder="e.g. Late Payment Fee, NSF Fee, Processing Fee"
                value={transactionForm.customFeeName || ''}
                onChange={(e) => setTransactionForm({...transactionForm, customFeeName: e.target.value})}
                required
              />
            </div>
          )}

          {/* DEFERRED FEE — source-deal picker + amount-outstanding readout (Feb 2026) */}
          {isDeferredFee && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3" data-testid="defer-fee-section">
              <p className="text-xs font-medium text-amber-800">
                Deferred Fee — formally moves outstanding fees from a SOURCE deal to this deal.
                Both legs are posted atomically; the source deal's fee math is offset.
              </p>
              <div>
                <Label>Source Deal <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Search by deal ID, name, or shortcode..."
                  value={sourceQuery}
                  onChange={(e) => setSourceQuery(e.target.value)}
                  data-testid="defer-source-search"
                  className="bg-white"
                />
                {sourceQuery && filteredCandidates.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded max-h-40 overflow-y-auto bg-white shadow-sm">
                    {filteredCandidates.map(d => (
                      <button
                        type="button"
                        key={d.id}
                        onClick={() => { setSelectedSourceId(d.id); setSourceQuery(`#${d.deal_id} — ${d.deal_name || ''}`); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex justify-between"
                        data-testid={`defer-source-option-${d.deal_id}`}
                      >
                        <span>#{d.deal_id} {d.deal_name && `· ${d.deal_name}`}</span>
                        <span className="text-slate-500 text-xs">{d.status}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedSourceId && sourceDeal && (
                  <p className="text-xs text-slate-700 mt-1" data-testid="defer-source-selected">
                    Selected: <strong>#{sourceDeal.deal_id}</strong> · {sourceDeal.deal_name || sourceDeal.merchant_name}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount Outstanding</Label>
                  <Input
                    readOnly
                    value={selectedSourceId ? `$${sourceFeesAvailable.toFixed(2)}` : '—'}
                    className="bg-white font-semibold"
                    data-testid="defer-source-fees-readout"
                  />
                </div>
                <div>
                  <Label htmlFor="deferAmount">Defer Amount <span className="text-red-500">*</span></Label>
                  <Input
                    id="deferAmount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={deferAmount}
                    onChange={(e) => setDeferAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="defer-amount-input"
                    className="bg-white"
                  />
                </div>
              </div>
              {selectedSourceId && sourceFeesAvailable === 0 && (
                <p className="text-xs text-red-700">Source deal has no outstanding fees to defer.</p>
              )}
            </div>
          )}

          {/* Amount */}
          {transactionForm.type !== 'LAWYER_COLLECTION' && !isDeferredFee && (
            <div>
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                data-testid="transaction-amount-input"
                type="number" step="0.01"
                value={transactionForm.amount}
                onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                required
              />
              {(transactionForm.type === 'REFINANCE_PAYOFF' || transactionForm.type === 'EARLY_PAYOFF') && (
                <p className="text-xs text-blue-600 mt-1">
                  Auto-filled with effective balance (includes fees): {formatCurrency(deal?.balance_with_fees || deal?.balance)}
                </p>
              )}
            </div>
          )}

          {/* Bounce Fee Section */}
          {transactionForm.type === 'ACH_RETURN' && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox" id="addBounceFee"
                  data-testid="bounce-fee-checkbox"
                  checked={transactionForm.addBounceFee}
                  onChange={(e) => setTransactionForm({...transactionForm, addBounceFee: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="addBounceFee" className="text-sm font-medium cursor-pointer">Add Bounce Fee</Label>
              </div>
              {transactionForm.addBounceFee && (
                <div>
                  <Label htmlFor="bounceFeeAmount" className="text-xs text-gray-600">Fee Amount</Label>
                  <Input
                    id="bounceFeeAmount"
                    data-testid="bounce-fee-amount-input"
                    type="number" step="0.01"
                    value={transactionForm.bounceFeeAmount}
                    onChange={(e) => setTransactionForm({...transactionForm, bounceFeeAmount: e.target.value})}
                    className="h-8 mt-1"
                  />
                </div>
              )}
            </div>
          )}

          {/* Date */}
          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date" type="date"
              value={transactionForm.date}
              onChange={(e) => {
                const newDate = e.target.value;
                const newForm = { ...transactionForm, date: newDate };
                const desc = transactionForm.description;
                if ((desc === 'Zelle' || desc === 'Venmo') && ZELLE_VENMO_REF_RE.test(transactionForm.reference_number)) {
                  newForm.reference_number = `${desc}-${dateToRefFormat(newDate)}`;
                }
                setTransactionForm(newForm);
              }}
              required
            />
          </div>

          {/* Reference # */}
          <div>
            <Label htmlFor="reference">
              Reference #
              {transactionForm.type === 'Discount' && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id="reference"
              value={transactionForm.reference_number}
              onChange={(e) => setTransactionForm({...transactionForm, reference_number: e.target.value})}
              placeholder="Optional (auto-generated if empty)"
            />
            {(transactionForm.type === 'Discount' || transactionForm.type === 'Fee Discount') && !transactionForm.reference_number && (
              <p className="text-xs text-gray-500 mt-1">Reference will be auto-generated if left blank</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (Admin only)</Label>
            <textarea
              id="notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={3}
              value={transactionForm.notes}
              onChange={(e) => setTransactionForm({...transactionForm, notes: e.target.value})}
              placeholder="Add internal notes (only visible to admins)"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => { setTransactionDialogOpen(false); setEditingTransaction(null); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submittingDefer} data-testid={isDeferredFee ? 'defer-submit-btn' : 'transaction-submit-btn'}>
              {isDeferredFee
                ? (submittingDefer ? 'Deferring...' : 'Defer Fee')
                : `${editingTransaction ? 'Update' : 'Add'} Transaction`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionFormDialog;
